import type { ActivityRange, DeskMetric, DeskPulse } from '@deskworks/shared-types';
import { ACTIVITY_RANGES } from '@deskworks/shared-types';
import { CircleCheck, CircleDot, CircleX, LoaderCircle } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { intlLocale, t, tr } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/** Bucket length per window, as the Control Plane counts them (docs/features/desk-pulse.md). */
export const BUCKET_SECONDS: Record<ActivityRange, number> = { '24h': 3600, '7d': 6 * 3600, '30d': 86400 };
const WINDOW_DAYS: Record<ActivityRange, number> = { '24h': 1, '7d': 7, '30d': 30 };
const RANGE_LABEL: Record<ActivityRange, string> = { '24h': t('last 24 h'), '7d': t('last 7 days'), '30d': t('last 30 days') };

export type CardView = 'activity' | 'metric';

/**
 * A per-viewer choice shared by every desk card on the page (so desks compare at a glance), remembered in the browser.
 */
function choice<T extends string>(key: string, initial: T, valid: readonly T[]) {
  let value = initial;
  try {
    const stored = localStorage.getItem(key) as T | null;
    if (stored && valid.includes(stored)) value = stored;
  } catch {
    // no storage (private window, blocked): the default holds
  }
  const listeners = new Set<() => void>();
  const set = (next: T) => {
    value = next;
    try {
      localStorage.setItem(key, next);
    } catch {
      // not remembered, still applied
    }
    listeners.forEach((l) => l());
  };
  const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  };
  return () => [useSyncExternalStore(subscribe, () => value), set] as const;
}

export const useActivityRange = choice<ActivityRange>('deskworks.desk-card.range', '24h', ACTIVITY_RANGES);
export const useCardView = choice<CardView>('deskworks.desk-card.view', 'activity', ['activity', 'metric']);

export function rangeLabel(range: ActivityRange) {
  return RANGE_LABEL[range];
}

/** The metric's points within the window; all of them when the window holds fewer than two. */
export function metricInRange(metric: DeskMetric, range: ActivityRange): DeskMetric {
  const since = Date.now() - WINDOW_DAYS[range] * 86400_000;
  const points = metric.points.filter((p) => new Date(p.at).getTime() >= since);
  return points.length >= 2 ? { ...metric, points, latest: points.at(-1) ?? metric.latest } : metric;
}

function bucketTitle(range: ActivityRange, back: number, n: number) {
  const now = Date.now();
  const end = new Date(now - back * BUCKET_SECONDS[range] * 1000);
  const start = new Date(end.getTime() - BUCKET_SECONDS[range] * 1000);
  const time = (d: Date) => d.toLocaleTimeString(intlLocale, { hour: '2-digit', minute: '2-digit', hour12: false });
  const day = (d: Date) => d.toLocaleDateString(intlLocale, { weekday: 'short', month: 'short', day: 'numeric' });
  const when =
    range === '24h' ? `${time(start)}–${back === 0 ? t('now') : time(end)}` : range === '7d' ? `${day(start)}, ${time(start)}–${back === 0 ? t('now') : time(end)}` : back === 0 ? t('last 24 h') : day(start);
  return n === 1 ? t('{when} · 1 event', { when }) : t('{when} · {n} events', { when, n });
}

/** The desk's activity stream: one bar per bucket, oldest left; the current bucket glows while the desk is busy. */
export function ActivityBars({ buckets, range, live, className }: { buckets: number[]; range: ActivityRange; live?: boolean; className?: string }) {
  const max = Math.max(1, ...buckets);
  return (
    <div className={cn('flex h-9 items-end gap-[2px]', className)} role="img" aria-label={t('{n} events, {range}', { n: buckets.reduce((a, b) => a + b, 0), range: rangeLabel(range) })}>
      {buckets.map((n, i) => {
        const back = buckets.length - 1 - i;
        const last = back === 0;
        return (
          <span key={i} title={bucketTitle(range, back, n)} className="flex h-full min-w-0 flex-1 items-end">
            <span
              className={cn(
                'w-full rounded-t-[2px] bg-current transition-[height] duration-500',
                n === 0 ? 'h-[2px] opacity-20' : last ? 'opacity-100' : 'opacity-60',
                last && live && 'animate-soft-pulse shadow-[0_0_8px_1px_currentColor]',
              )}
              style={n === 0 ? undefined : { height: `${Math.max(12, (n / max) * 100)}%` }}
            />
          </span>
        );
      })}
    </div>
  );
}

/** "2 in progress · 3 open · 12 done · 1 failed": the desk's Work Items worked on now, open now and finished within the window. */
export function WorkItemCounts({ items, range, className }: { items: DeskPulse['workItems'] | undefined; range: ActivityRange; className?: string }) {
  const active = items?.active ?? 0;
  const running = items?.running ?? 0;
  const done = items?.done[range] ?? 0;
  const failed = items?.failed[range] ?? 0;
  const label = rangeLabel(range);
  return (
    <div className={cn('flex items-center gap-3 text-[11px] text-muted-foreground tabular-nums', className)}>
      <span className="inline-flex items-center gap-1" title={running === 1 ? t('An agent works on 1 Work Item now') : t('Agents work on {n} Work Items now', { n: running })}>
        <LoaderCircle className={cn('size-3 text-amber-500', running > 0 && 'animate-spin [animation-duration:2.5s]')} />
        {tr('{n} in progress', { n: <span className="font-medium text-foreground">{running}</span> })}
      </span>
      <span className="inline-flex items-center gap-1" title={active === 1 ? t('1 open Work Item now') : t('{n} open Work Items now', { n: active })}>
        <CircleDot className="size-3 text-sky-500" />
        {tr('{n} open', { n: <span className="font-medium text-foreground">{active}</span> })}
      </span>
      <span className="inline-flex items-center gap-1" title={t('{n} completed · {range}', { n: done, range: label })}>
        <CircleCheck className="size-3 text-emerald-500" />
        {tr('{n} done', { n: <span className="font-medium text-foreground">{done}</span> })}
      </span>
      {failed > 0 && (
        <span className="inline-flex items-center gap-1" title={t('{n} failed · {range}', { n: failed, range: label })}>
          <CircleX className="size-3 text-red-500" />
          {tr('{n} failed', { n: <span className="font-medium text-foreground">{failed}</span> })}
        </span>
      )}
    </div>
  );
}

function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border bg-card/95 p-0.5 shadow-sm backdrop-blur">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'max-w-28 truncate rounded px-1.5 py-px text-[10px] font-medium transition-colors',
            value === o.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function RangeSwitch({ className }: { className?: string }) {
  const [range, setRange] = useActivityRange();
  return (
    <span className={className}>
      <Segmented label={t('Time window')} value={range} onChange={setRange} options={ACTIVITY_RANGES.map((r) => ({ value: r, label: r }))} />
    </span>
  );
}

export function ViewSwitch({ metricName, className }: { metricName: string; className?: string }) {
  const [view, setView] = useCardView();
  return (
    <span className={className}>
      <Segmented
        label={t('Show')}
        value={view}
        onChange={setView}
        options={[
          { value: 'activity', label: t('Activity') },
          { value: 'metric', label: metricName },
        ]}
      />
    </span>
  );
}
